<template>
  <div class="form-grid">
    <div>
      <label for="industry" class="field-label">Industry</label>
      <select
        id="industry"
        v-model="formData.selectedIndustry"
        @change="fetchRoles"
        class="field-input"
      >
        <option disabled selected value="">Select Industry</option>
        <option v-for="industry in industries" :key="industry.id" :value="industry.id">
          {{ industry.name }}
        </option>
      </select>
    </div>

    <div>
      <label for="role" class="field-label">Select Role</label>
      <select
        id="role"
        v-model="roleSelectValue"
        @change="fetchQuestions"
        :aria-disabled="isRoleSelectDisabled"
        :class="[
          'field-input',
          { 'field-input--disabled': isRoleSelectDisabled }
        ]"
      >
        <option disabled :value="placeholderOptionValue">{{ rolePlaceholder }}</option>
        <template v-if="hasRoleCategories">
          <optgroup
            v-for="group in groupedRoles"
            :key="group.name"
            :label="group.name"
          >
            <option v-for="role in group.roles" :key="role.id" :value="role.id">
              {{ role.name }}
            </option>
          </optgroup>
        </template>
        <template v-else>
          <option v-for="role in roles" :key="role.id" :value="role.id">
            {{ role.name }}
          </option>
        </template>
      </select>
    </div>
  </div>
</template>

<script>
import genericService from '../../../../services/marketplace/generic.service';

export default {
  props: {
    formData: {
      type: Object,
      required: true
    },
    fetchRoleQuestions: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      industries: [],
      roles: [],
      rolesLoading: false,
      placeholderOptionValue: '__role_placeholder__'
    }
  },
  computed: {
    isRoleSelectDisabled() {
      return !this.formData.selectedIndustry || this.rolesLoading || !this.roles.length;
    },
    rolePlaceholder() {
      if (!this.formData.selectedIndustry) {
        return 'Select an industry first';
      }
      if (this.rolesLoading) {
        return 'Loading roles...';
      }
      if (!this.roles.length) {
        return 'No roles available yet';
      }
      return 'Select a role';
    },
    roleSelectValue: {
      get() {
        if (this.isRoleSelectDisabled || !this.formData.selectedRole) {
          return this.placeholderOptionValue;
        }
        return this.formData.selectedRole;
      },
      set(value) {
        if (value === this.placeholderOptionValue) {
          this.formData.selectedRole = '';
          return;
        }
        this.formData.selectedRole = value;
      }
    },
    hasRoleCategories() {
      return this.roles.some((role) => role.category_name);
    },
    groupedRoles() {
      const groups = [];
      const groupMap = new Map();

      this.roles.forEach((role) => {
        const categoryName = role.category_name || 'Other roles';

        if (!groupMap.has(categoryName)) {
          const group = { name: categoryName, roles: [] };
          groupMap.set(categoryName, group);
          groups.push(group);
        }

        groupMap.get(categoryName).roles.push(role);
      });

      return groups;
    }
  },
  methods: {
    initiateDefaultValues() {
      this.formData.selectedIndustry = this.formData.selectedIndustry !== undefined ? this.formData.selectedIndustry : '';
      this.formData.selectedRole = this.formData.selectedRole !== undefined ? this.formData.selectedRole : '';
      this.formData.questions = this.formData.questions !== undefined ? this.formData.questions : [];
      this.formData.answers = this.formData.answers !== undefined ? this.formData.answers : [];
    },
    async fetchIndustries() {
      try {
        const response = await genericService.getIndustries();
        this.industries = response.data;
      } catch (error) {
        console.error('Error fetching industries:', error);
      }
    },
    async fetchRoles(preserveSelection = false) {
      const previousRole = preserveSelection ? this.formData.selectedRole : '';

      this.roles = [];
      this.rolesLoading = !!this.formData.selectedIndustry;
      if (!preserveSelection) {
        this.formData.selectedRole = '';
        this.formData.questions = [];
        this.formData.answers = [];
      }

      if (!this.formData.selectedIndustry) {
        this.rolesLoading = false;
        return;
      }

      try {
        const response = await genericService.getRoles(this.formData.selectedIndustry);
        this.roles = response.data;
      } catch (error) {
        console.error('Error fetching roles:', error);
      } finally {
        this.rolesLoading = false;
      }

      if (preserveSelection && previousRole) {
        const match = this.roles.find(role => role.id === previousRole);
        if (match) {
          this.formData.selectedRole = previousRole;
          await this.fetchQuestions(true);
        } else {
          this.formData.selectedRole = '';
          this.formData.questions = [];
          this.formData.answers = [];
        }
      }
    },
    async fetchQuestions(preserveAnswers = false) {
      if (!this.fetchRoleQuestions) {
        return;
      }

      const previousMap = {};
      if (preserveAnswers && Array.isArray(this.formData.questions)) {
        const prevAnswersArray = Array.isArray(this.formData.answers) ? this.formData.answers : [];
        this.formData.questions.forEach((question, index) => {
          previousMap[question.id] = prevAnswersArray[index] || '';
        });
      }

      this.formData.questions = [];
      this.formData.answers = [];

      if (this.formData.selectedRole) {
        try {
          const response = await genericService.getQuestions(this.formData.selectedRole);
          this.formData.questions = response.data;
          this.formData.answers = this.formData.questions.map((question) => {
            if (preserveAnswers && Object.prototype.hasOwnProperty.call(previousMap, question.id)) {
              return previousMap[question.id];
            }
            return '';
          });
          if (!Array.isArray(this.formData.answers)) {
            this.formData.answers = [this.formData.answers];
          }
        } catch (error) {
          console.error('Error fetching questions:', error);
        }
      }
    }
  },
  async mounted() {
    this.initiateDefaultValues();
    await this.fetchIndustries();
    if (this.formData.selectedIndustry) {
      await this.fetchRoles(true);
    }
  }
};
</script>

<style scoped>
.form-grid {
  @apply grid gap-6 md:grid-cols-2;
}

.field-label {
  @apply text-sm font-medium text-slate-700;
}

.field-input {
  @apply mt-2 w-full appearance-none rounded-md border border-gray-300 px-4 py-3 text-sm text-gray-700 transition focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300;
}

.field-input--disabled {
  @apply bg-gray-50 text-gray-500 cursor-default;
}
</style>
